import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, scrollToId } from "../../lib/site";

export const Logo = ({ onClick }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid="nav-logo"
        className="flex items-center gap-2.5"
        aria-label="NEBOORZY — retour à l'accueil"
    >
        <span className="grid h-8 w-8 place-items-center rounded-md bg-signal font-display text-lg font-black text-ink">
            N
        </span>
        <span className="font-display text-xl font-extrabold tracking-tight text-white">
            NEBOORZY
        </span>
    </button>
);

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300 ${
                    scrolled || open
                        ? "border-b border-white/10 bg-ink/90 backdrop-blur-xl"
                        : "border-b border-transparent bg-transparent"
                }`}
            >
                <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
                    <Logo onClick={() => go("accueil")} />
                    <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigation principale">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.id}
                                type="button"
                                data-testid={l.testid}
                                onClick={() => go(l.id)}
                                className="text-sm font-medium text-zinc-300 transition-colors hover:text-white"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="nav-cta-demander-commercial"
                            onClick={() => go("besoin")}
                            className="hidden items-center gap-2 rounded-full bg-signal px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-signal-dark sm:inline-flex"
                        >
                            Demander un commercial
                            <ArrowUpRight className="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            data-testid="nav-mobile-menu-button"
                            onClick={() => setOpen((v) => !v)}
                            className="grid h-10 w-10 place-items-center rounded-md border border-white/15 text-white lg:hidden"
                            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                        >
                            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </button>
                    </div>
                </div>
                <AnimatePresence>
                    {open && (
                        <motion.nav
                            data-testid="nav-mobile-menu"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden border-t border-white/10 bg-ink lg:hidden"
                            aria-label="Navigation mobile"
                        >
                            <div className="flex flex-col px-5 py-4">
                                {NAV_LINKS.map((l) => (
                                    <button
                                        key={l.id}
                                        type="button"
                                        data-testid={`nav-mobile-${l.id}`}
                                        onClick={() => go(l.id)}
                                        className="border-b border-white/5 py-4 text-left font-display text-lg font-semibold text-white"
                                    >
                                        {l.label}
                                    </button>
                                ))}
                            </div>
                        </motion.nav>
                    )}
                </AnimatePresence>
            </header>
            <div className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-ink/95 p-3 backdrop-blur-xl sm:hidden">
                <button
                    type="button"
                    data-testid="mobile-sticky-cta"
                    onClick={() => go("besoin")}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-signal py-3.5 text-sm font-bold text-ink"
                >
                    Demander un commercial
                    <ArrowUpRight className="h-4 w-4" />
                </button>
            </div>
        </>
    );
};
